// Semantic theme tokens for the block renderers.
//
// Each is a CSS custom property (injected by the tenant layout from the site's
// `style`, and — once wired — by the builder preview) with a BRAND-DEFAULT
// fallback. The fallback means a block still renders correctly where the var
// isn't defined (e.g. an unthemed preview), while a themed surface recolors the
// whole site the moment the palette changes. Blocks must use these instead of
// hardcoded Tailwind colors so the Brand Manager palette actually drives them.
//
// Mapping intent:
//  - secondary  → dark brand surfaces (hero band, footer)
//  - primary    → the action color (CTA band, buttons, links, active states)
//  - surface    → light section fills (search bar, cards)
//  - background → page/header background
//  - text/textMuted → body + secondary text on light surfaces
// Dark surfaces (secondary/primary) pair with white text by design.
/**
 * Resolve a section's background + text color: a per-block override (hex) wins,
 * else the brand-theme default. Main text elements should INHERIT this `color`
 * (omit their own) so a custom textColor actually applies; muted/secondary text
 * can keep an `opacity-*` treatment so it stays legible on any base color.
 */
export function sectionColors(
  props: { backgroundColor?: string; textColor?: string },
  defaults: { bg: string; text: string },
): { backgroundColor: string; color: string } {
  return {
    backgroundColor: props.backgroundColor?.trim() ? props.backgroundColor : defaults.bg,
    color: props.textColor?.trim() ? props.textColor : defaults.text,
  };
}

export const theme = {
  primary: 'var(--color-primary, #4E1F60)',
  secondary: 'var(--color-secondary, #10032A)',
  accent: 'var(--color-accent, #C9B8F4)',
  background: 'var(--color-background, #ffffff)',
  surface: 'var(--color-surface, #f8f6ff)',
  text: 'var(--color-text, #10032A)',
  textMuted: 'var(--color-text-muted, #6b7280)',
  // Readable text ON the primary/secondary brand surfaces — the tenant layout injects these from
  // each color's WCAG luminance, so white text never lands on a LIGHT brand (the invisible
  // footer-text bug on light-tan secondary). Fall back to white for the common dark-brand case.
  onPrimary: 'var(--color-on-primary, #ffffff)',
  onSecondary: 'var(--color-on-secondary, #ffffff)',
  headingFont: 'var(--font-heading, inherit)',
  bodyFont: 'var(--font-body, inherit)',
} as const;
